/**
 * Mede quanto o índice "Artefatos desta conversa" pode ter de largura sem
 * cobrir o trilho — e mantém a medida em dia (§5.1 do CLAUDE.md do módulo).
 *
 * A casca do índice é do `messages.vue` (sticky, absoluta no canto), e só
 * ele enxerga as duas medidas que importam: o `clientWidth` do container que
 * rola (sem a barra de rolagem) e a largura REAL do trilho (`$refs.trilho`
 * — não o token `--ia-chat-trilho-largura`: ele é um `max-width`, e num
 * container mais estreito o trilho é `width: 100%`). A conta é o util puro
 * `larguraMaximaDoIndiceDeArtefatos`; o resultado vai ao componente pela
 * prop `larguraMaxima`, e é ELE quem decide menu limitado ou régua.
 *
 * `ResizeObserver` no CONTAINER (o outro observador do `messages.vue` olha o
 * trilho, para rolar): abrir/recolher a lateral, abrir o aside do artefato
 * e redimensionar a janela mudam a largura sem nenhum evento próprio. Mesma
 * receita rAF do observador de conteúdo — medir dentro do callback reentra
 * no layout e dispara o warning `ResizeObserver loop…`, que aqui reprova
 * teste. Guardado por `typeof`: não existe no jsdom. Sem trilho a medida é
 * `null` — e com `null` o `messages.vue` NÃO monta o índice (ver
 * `mostraIndiceDeArtefatos`): ele só entra com a faixa conhecida.
 *
 * Mixin, e não métodos no `messages.vue`: o arquivo já passa de 1000 linhas.
 */
import { larguraMaximaDoIndiceDeArtefatos } from '@modules/aawz-intelligence-v2/utils/largura-do-indice-de-artefatos'

export default {
  data() {
    return {
      // `null` = ainda não medido (ou sem trilho): o índice não se limita.
      larguraMaximaDoIndiceDeArtefatos: null,
      // Handles — só para desligar em `beforeDestroy`.
      observadorDeLargura: null,
      larguraRafId: null,
    }
  },
  watch: {
    // O trilho é recriado pelo `v-if` (conversa nova, primeira mensagem) —
    // medir quando ele está no DOM. NÃO se mede no `mostraIndiceDeArtefatos`:
    // ele só liga com a medida presente (o índice espera a carga E a
    // medida para entrar já na visão certa), seria um ciclo.
    mostraTrilho(mostra) {
      if (!mostra) return
      this.$nextTick(() => this.medirLarguraDoIndiceDeArtefatos())
    },
    // Fim da carga da conversa: o trilho já assentou — remedir antes de o
    // índice entrar (o `v-if` do índice depende da medida).
    carregandoMensagens(carregando) {
      if (carregando) return
      this.$nextTick(() => this.medirLarguraDoIndiceDeArtefatos())
    },
    // A primeira entrega de uma conversa VAZIA (upload de carteira): não há
    // trilho para disparar a medida, e sem ela o índice não entra. Pelo
    // TAMANHO, não pela lista: o getter é reordenado a cada mensagem nova e
    // dispararia uma medição por turno.
    'artefatosDaConversa.length'(quantidade) {
      if (!quantidade) return
      this.$nextTick(() => this.medirLarguraDoIndiceDeArtefatos())
    },
  },
  mounted() {
    this.observarLarguraDoContainer()
  },
  beforeDestroy() {
    if (this.larguraRafId !== null) cancelAnimationFrame(this.larguraRafId)
    if (this.observadorDeLargura) {
      this.observadorDeLargura.disconnect()
      this.observadorDeLargura = null
    }
  },
  methods: {
    observarLarguraDoContainer() {
      if (typeof ResizeObserver === 'undefined') return
      this.observadorDeLargura = new ResizeObserver(() => {
        if (this.larguraRafId !== null) return
        this.larguraRafId = requestAnimationFrame(() => {
          this.larguraRafId = null
          this.medirLarguraDoIndiceDeArtefatos()
        })
      })
      const container = this.$refs.scrollContainer
      if (container) this.observadorDeLargura.observe(container)
    },
    medirLarguraDoIndiceDeArtefatos() {
      const container = this.$refs.scrollContainer
      if (!container) {
        this.larguraMaximaDoIndiceDeArtefatos = null
        return
      }
      const trilho = this.$refs.trilho
      // [15/09/2026] Sem trilho (conversa VAZIA) a faixa é medida pela coluna
      // do estado vazio: um artefato pode nascer antes da primeira mensagem
      // (o upload de carteira da Home IA) e o índice precisa aparecer ali —
      // antes, a medida nula escondia o índice e o aside fechado não tinha
      // por onde reabrir.
      const larguraDoTrilho = trilho
        ? trilho.getBoundingClientRect().width
        : this.larguraDaColunaDoVazio(container)
      this.larguraMaximaDoIndiceDeArtefatos = larguraMaximaDoIndiceDeArtefatos({
        larguraDoContainer: container.clientWidth,
        larguraDoTrilho: larguraDoTrilho,
      })
    },
    // A coluna do vazio é `--ia-chat-trilho-largura-vazio` (`_ia-chat-trilho.sass`),
    // lida do container para não redigitar o número; sem a variável (jsdom,
    // superfície sem a folha) vale a largura inteira — o índice vira régua.
    larguraDaColunaDoVazio(container) {
      const bruto = getComputedStyle(container).getPropertyValue(
        '--ia-chat-trilho-largura-vazio'
      )
      const px = parseFloat(bruto)
      if (!Number.isFinite(px)) return container.clientWidth
      return Math.min(container.clientWidth, px)
    },
  },
}
